// Generic tag/genre chip-input backed by the server-side /tags/suggest endpoint (ranked, existing
// tags/genres/studios used anywhere in the library) - lets the manual-add-media form suggest tags
// that already exist so the user can pick them instead of retyping free-form variants, while Enter
// on anything not in the list still adds it as a brand new tag. Reuses TagFilterInput's CSS classes
// (App.css) since visually it's the same chip-input widget, just with server-side suggestions.
import React, { useState, useMemo, useRef, useEffect } from 'react';
import debounce from 'lodash/debounce';

const TagInput = ({ apiBaseUrl, selected, onChange, placeholder }) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const inputRef = useRef(null);

  const fetchSuggestions = useMemo(
    () =>
      debounce((q) => {
        fetch(`${apiBaseUrl}/tags/suggest?q=${encodeURIComponent(q)}`, { credentials: 'include' })
          .then((res) => (res.ok ? res.json() : []))
          .then((data) => setSuggestions(Array.isArray(data) ? data : []))
          .catch(() => setSuggestions([]));
      }, 250),
    [apiBaseUrl]
  );

  useEffect(() => {
    fetchSuggestions(query);
    return () => fetchSuggestions.cancel();
  }, [query, fetchSuggestions]);

  const visibleSuggestions = suggestions.filter(
    (s) => !selected.some((v) => v.toLowerCase() === s.toLowerCase())
  );

  useEffect(() => {
    setHighlightedIndex(0);
  }, [visibleSuggestions.length]);

  const addTag = (raw) => {
    const value = raw.trim();
    if (!value || selected.some((v) => v.toLowerCase() === value.toLowerCase())) {
      setQuery('');
      return;
    }
    onChange([...selected, value]);
    setQuery('');
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  const removeTag = (value) => onChange(selected.filter((v) => v !== value));

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (visibleSuggestions.length === 0) return;
      setShowSuggestions(true);
      setHighlightedIndex((prev) => (prev + 1) % visibleSuggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (visibleSuggestions.length === 0) return;
      setShowSuggestions(true);
      setHighlightedIndex((prev) => (prev - 1 + visibleSuggestions.length) % visibleSuggestions.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const chosen = visibleSuggestions.length > 0 ? visibleSuggestions[highlightedIndex] ?? visibleSuggestions[0] : query;
      addTag(chosen);
    } else if (e.key === 'Backspace' && !query && selected.length > 0) {
      removeTag(selected[selected.length - 1]);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      inputRef.current?.blur();
    }
  };

  return (
    <div className="tag-filter-input">
      {selected.length > 0 && (
        <div className="tag-filter-chips">
          {selected.map((value) => (
            <span className="tag-chip" key={value}>
              {value}
              <button
                type="button"
                className="tag-chip-remove"
                onClick={() => removeTag(value)}
                aria-label={`Remove ${value}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="tag-filter-input-wrapper">
        <input
          ref={inputRef}
          type="text"
          className="input-field tag-filter-text-input"
          placeholder={placeholder || 'Search or add a tag...'}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setShowSuggestions(true);
          }}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
          onKeyDown={handleKeyDown}
          role="combobox"
          aria-expanded={showSuggestions && visibleSuggestions.length > 0}
          aria-autocomplete="list"
        />
        {showSuggestions && visibleSuggestions.length > 0 && (
          <ul className="tag-filter-suggestions">
            {visibleSuggestions.map((value, index) => (
              <li
                key={value}
                className={index === highlightedIndex ? 'active' : ''}
                onMouseDown={() => addTag(value)}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                <span className="tag-filter-suggestion-label">{value}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default TagInput;
